// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

<template>
<!-- [guanxing patch] M-SW38：导出独属于你的星图（用户需求：两张面朝天空的
     全景截图，囊括整个星空，边框是地景；一张星座连线、一张中国星官连线，
     不开艺术图；先预览后手动下载——AskUserQuestion 定案）。 -->
<v-dialog max-width="920" v-model="$store.state.showStarMapExportDialog">
  <v-card v-if="$store.state.showStarMapExportDialog" class="secondary white--text">
    <v-card-title>
      <div class="text-h5">把此刻的星空带走</div>
    </v-card-title>
    <v-card-text>
      <div class="gx-export-hint">
        镜头仰起，正对你头顶的整片星空——这一刻，此地，只属于你。
      </div>
      <!-- [guanxing patch] M-SW57：上次导出若被系统杀页中断，这里显示中断阶段（真机诊断）。 -->
      <div v-if="staleNotice" class="gx-export-stale">{{ staleNotice }}</div>
      <div v-if="busy" class="gx-export-busy">
        <v-progress-linear indeterminate color="white"></v-progress-linear>
        <div style="margin-top: 8px;">正在为你定格这片星空…</div>
      </div>
      <div v-else-if="error" class="gx-export-error">
        这一夜的星空没能留住：{{ error }}
        <div style="margin-top: 6px;">画面已回到原来的样子，可以再来一次</div>
      </div>
      <template v-else-if="westImg && chineseImg">
        <v-layout wrap>
          <v-flex xs12 sm6 class="gx-export-cell">
            <img :src="westImg" alt="西方星座星图" class="gx-export-img">
            <div class="gx-export-caption">西方星座</div>
            <v-btn dark block outline @click="save(westImg, '星图-星座连线.png')">保存这一张</v-btn>
          </v-flex>
          <v-flex xs12 sm6 class="gx-export-cell">
            <img :src="chineseImg" alt="华夏星官星图" class="gx-export-img">
            <div class="gx-export-caption">华夏星官</div>
            <v-btn dark block outline @click="save(chineseImg, '星图-星官连线.png')">保存这一张</v-btn>
          </v-flex>
        </v-layout>
        <!-- [guanxing patch] M-SW55：iOS 一次手势只允许一次系统分享；「两张都保存」
             的第二张被拦下时在这里给出「再点一次」的引导。 -->
        <div v-if="hint" class="gx-export-hint-err">{{ hint }}</div>
      </template>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn v-if="!busy && westImg && chineseImg" dark @click="saveAll">两张都保存</v-btn>
      <v-btn v-if="!busy" text dark @click="close">关闭</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
</template>

<script>
import swh from '@/assets/sw_helpers.js'
import { appendFooter, footerUrl } from '@/assets/image-footer.js'
import { mjdToUnixMs } from '@/assets/image-date.js'
import { saveImage } from '@/assets/image-save.js'

// [guanxing patch] M-SW38：导出星图。整体流程（全部应用层实现，零引擎重编译）：
//
// 截图取像——引擎 WebGL 上下文 preserveDrawingBuffer=false（vendor/src/js/pre.js），
// 事后 toDataURL 只会拿到黑帧。渲染循环（canvas.js）每帧经 Module._core_render
// 动态取属性后调 wasm，因此在应用层把 $stel._core_render 包一层：原渲染返回后
// 的同一 JS 任务内同步 canvas.toDataURL()，缓冲区尚未被合成器清掉，取像有效。
// 导出结束把原函数装回去。
//
// 【M-SW38 修正：为什么必须清 lock】用户反馈「我想要的是拍下整个星空 视角朝向正
// 上方」。根因在引擎：只要此前点选过天体（点星 / 星命弹窗聚焦 / /skysource/ 直达），
// swh.setSweObjAsSelection → pointAndLock 会把 core.lock 设为该天体，此后 **每一帧**
// core_update_direction（navigation.c:116-123）都用该天体位置重写 observer.yaw/pitch
// ——导出前设的 pitch=90° 下一帧即被覆盖，拍出来是那颗星的方向而非天顶。修正：
// 导出期间保存并清空 core.lock 与 core.selection（顺带去掉选中高亮圈），拍完恢复；
// 另在渲染钩子里逐帧重设天顶朝向作双保险（引擎的视角动画也可能改写朝向）。
//
// 视角与图层——两张图共用同一套「天顶全景」场景：observer.pitch=90°、yaw=0（正上方、
// 朝向确定）、core.fov=188°（星空圆盘几乎铺满 + 一圈细地景边框；立体投影 max_fov=360°
// 可容）、地景全显（opacity=1 + hide_below_horizon=false，地平圆盘 size=π 覆盖整个地平线
// 以下，正好成为星空外的地景环）、星座连线+标签开、艺术图关、五类网格/刻度线临时关闭；
// 区别只在星空文化：图1 western（星座连线）、图2 `chinese`（中国星官连线）。
// fov>120° 会被 sw_helpers 的移动端钳制守卫弹回，走 setFovClampEnabled 旁路。
//
// 【M-SW38-b：用户三条反馈的修正】
// ① 「地景占比太大」——原 fov=205° 时按立体投影 r=tan(α/2)/tan(fov/4)，星空圆盘只占
//    画面半径的 80.2%，外面一圈地景占近 20%。收窄到 188° → 圆盘占 93.2%，地景环约 6.8%。
// ② 「地平线以下的星座名漏到圈外」——引擎标签是最后一道 2D 绘制且关掉深度测试，
//    地景模块又没实现遮挡接口（is_point_occulted 只有行星模块实现），故引擎只判断标签
//    是否在画面矩形内（labels.c: should_render_label → painter_is_point_clipped_fast），
//    **不判断地平线**，地平线以下的星座名/星官名就画到了黑盘上。修法：导出后在 2D 画布上
//    做圆形遮罩，把地平线投影圆以外填成地景底色（地景本就是近乎纯黑的圆盘，视觉无损），
//    一次性抹掉所有漏出的名称。半径由同一投影公式精确算出。
// ③ 「移动端截图文字太大」——标签字号是引擎固定常量 FONT_SIZE_BASE=15（窗口单位，
//    与屏幕无关），文字占画面比例 = 15 ÷ 画面宽（窗口单位）；手机画面窄（约 390）
//    比例达 3.8%，桌面（1440）只有约 1%，故手机上导出文字明显偏大。修法：导出期间把
//    canvas 的窗口单位固定为 EXPORT_SIZE（1600）——标签相对大小因此与设备无关。
//    方形窗口同时保证天顶全景完整、遮罩半径可精确计算；导出结束原样恢复。
//    （后备缓冲与设备 dpr 的解耦见文末 M-SW57。）
//
// 【M-SW38-c：用户反馈「截图 中国星官连线 时 连线不同」——两处修正】
// ① **图2 用错了星空文化**（本次反馈的直接原因）。界面底栏四态循环是
//    「关闭 → western（西方星座）→ chinese（**中国星官**，310 星官 / 424 段线）
//    → chinese-28（**二十八宿**，28 宿 / 59 段线）→ 关闭」（bottom-bar.vue
//    cycleConstellations），而导出图2 一直用的是 `chinese-28`——标题写「中国星官
//    连线」、画的却是二十八宿，与界面上看到的中国星官必然不同。修正：图2 改用
//    `chinese`，与界面「中国星官」同源；`chinese-28`（二十八宿）不再出现在导出里。
// ② **把「固定等 2s」换成就绪轮询**（AGENTS §6 N29 的时序缺口；310 星官数据量是
//    二十八宿的近 8 倍，固定等待在冷缓存/慢网下不可靠）。判据为什么这么取——
//    星座对象是引擎在**数据到货**时才由 skyculture_activate 注册的
//    （skycultures.c:123-151），数据没到货时切 current_id 是**空切换**，取像会得到
//    一张没有连线的图。实测（线上现役构建，本机 Chromium）：
//    · 「数星座对象个数」这条路不通——星座子对象 id 为 NULL，obj_foreach_child 与
//      module_get_tree 都跳过无 id 子对象，`core.constellations.getTree()` 只有
//      7 个属性键、0 个子对象；
//    · 可用的就绪信号是 `core.skycultures.current`——它返回**当前文化对象**
//      （含 name / url），切换前 `name="Western"`、切到 chinese 后 `name="中国星官"`；
//      `name` 在 index.json 解析成功时写入（skycultures.c:480）。
//    故判据 = 「current 对象可读 && name 非空 && current_id === 目标键」，再叠加
//    READY_SETTLE_MS 覆盖连线淡入。超时：信号在但名字始终为空 → **拒绝出图**
//    （宁可报错，不出缺线坏图，与 M-SW39 就绪门同纪律）；信号不可用（引擎结构
//    变化）→ 退化为固定等待并 warn，绝不把导出卡死。
//
// 页脚（M-SW41）——两张图各自在 postProcess 之后由 appendFooter 追加品牌 banner
// （图1 banners/star1.jpg、图2 banners/star2.png；等比缩到截图宽度后紧贴底部，
// 交接处 8px 羽化消接缝）。合成失败退回未加页脚的成图，不阻断导出。
//
// 右上角日期（M-SW63）——用户需求「三张生成的截图 右上角角落 加公历日期 格式为
// 1900-2-24 要很小很小 贴边」。两张星图各自把「这一帧的引擎时刻」交给
// appendFooter 的第三个参数（stampMs），由 assets/image-date.js 在**同一次画布
// 合成**里于右上角盖一枚极小日期：时刻源是 core.observer.utc（Stellarium 口径
// 为 MJD），故出生信息提交后冻结在出生那一刻时，日期即出生日期；把时间拖到
// 别的日子后随之改变。换算与档位（图宽比例、贴边距、32% 白）全在 image-date.js，
// 三张图共用同一套比例。日期取设备本地时区（AskUserQuestion 定案）。
//
// 状态恢复——视角（yaw/pitch/fov）、锁定与选中、星空文化（引擎 + store 镜像）、
// 连线/标签/艺术图、网格线、地景三态、canvas 窗口尺寸与后备尺寸，全部在 finally 里
// 按保存值写回。
//
// 【M-SW55：保存链路修复（保留）】旧版把数 MB 的 data:URL 塞进 <a href download>，
// iOS Safari 会当作「打开图片」，用户只能长按存到相册（用户报告的「图片存在相册里」）。
// 修法：保存统一走 image-save.js（iOS 弹系统分享面板 / 其它端 blob 下载）。
// 取像仍是钩子内**同步** canvas.toDataURL：preserveDrawingBuffer=false 下缓冲在本次
// 任务结束后才可能被清，异步 toBlob 的回调晚于清空、只会拿到黑帧，故不用 toBlob 取像。
//
// 【M-SW57：iPhone 导出崩溃的**真正**修法（M-SW55 的 dpr 方案已被真机证伪）】
// 症状：iPhone（iOS 17-18）点「把这片星空带走」**一点就崩、对话框都没出来、100% 复现**。
// 现场：线上包内确实含 M-SW55 的全部修复（navigator.canShare / revokeObjectURL /
//       dpr 探测 + 回缩），仍崩 ⇒ M-SW55 的根因判断（覆盖是否生效）不成立。
// 真因（下沉到引擎 C 源码 + 用「1600² 也崩」反推）：
//   · 后备缓冲(fb) = 窗口单位 × pixel_scale，而 pixel_scale 只能来自全局
//     window.devicePixelRatio（core.c:527-576 core_render(win_w, win_h, pixel_scale)
//     → painter.fb_size / render_prepare 的 rend->fb_size = {win_w*scale, win_h*scale}，
//     render_gl.c:1725 glViewport(0, 0, fb_size)）。
//   · 若全局 dpr 覆盖在 iOS **未生效**（由「1600² 也崩」反推成立：1600² = 2.56M 像素
//     比手机正常 1170×2532 = 2.96M 还小，正常浏览流畅不崩），则后备 = 1600×3 = **4800²
//     = 2300 万像素**；GL 上下文 antialias:true（pre.js:24）MSAA 4x + depth24stencil8
//     ⇒ 首帧分配近 730MB → iOS jetsam 杀页。M-SW55 的「探测失败 → 回缩 CSS 到 800」
//     得到 800×3 = 2400² ≈ 184MB，仍超限 ⇒ 降级力度不足，等于没修。
// 修法（**彻底绕开全局 dpr**，三处自持，见 beginExportSize / installCaptureHook）：
//   ① CSS 尺寸固定 EXPORT_SIZE 方形 → 窗口单位恒 1600（标签相对大小与设备无关，保住 M-SW38-b）；
//   ② 自己显式把后备缓冲钉为 canvas.width = canvas.height = EXPORT_SIZE；
//   ③ 给 canvas 实例打 getBoundingClientRect 补丁（宽高返回后备像素尺寸）→ 引擎
//      sizeChanged 判定恒 false，`canvas.width = rect.width * dpr` 永不执行；
//   ④ 渲染钩子里强制把 pixel_scale 传 1 → fb_size = 1600×1，与后备一致，viewport 亦 1600²。
//   结果：任何设备上后备恒 1600² = 手机平时正常渲染的量级；dpr 覆盖/探测/回缩全部删除。
// 真机面包屑：导出分阶段写 localStorage，若下次打开仍见未完成阶段 → 对话框里提示
//   「上次导出中断于 …」（真机再崩时能拿到确切位置，不用再猜）。
//
// 【M-SW58：恢复高清（把 M-SW57 一刀切压平的清晰度还回来）】
// 现象：M-SW57 上线后真机「正常成功了」，但**所有设备**的导出都被钉在 1600²——桌面
//   Retina 上原本的 3200² 一并丢失（用户要求「恢复高清图片」）。
// 硬约束（决定高清天花板，不可绕过）：iOS/Safari 对**单个 canvas** 有 16,777,216 px
//   （4096²）面积硬限，越界即静默失效或被 Jetsam 秒杀。M-SW55 的 1600×3 = 4800² =
//   2306 万像素正踩此线——这是「一点导出就崩」的直接真因（比内存更硬），4800² 永久排除。
// 修法（仍是纯应用层，零引擎重编译）：把「逻辑窗口」与「物理像素」拆开——
//   · EXPORT_SIZE = 1600：逻辑窗口单位，投影与标签比例的唯一基准（M-SW38-b 契约不变）；
//   · EXPORT_SCALE：像素倍率，桌面 2×（3200²）、移动 1.5×（2400²）；
//   · 后备缓冲 = EXPORT_SIZE × SCALE，rect 补丁同步返回该值（引擎 sizeChanged 恒 false）；
//   · 渲染钩子把引擎入参还原为 `(EXPORT_SIZE, EXPORT_SIZE, SCALE)` ⇒
//     fb_size = 1600×SCALE = 后备边长，glViewport 一致；投影仍按 1600 算，标签大小不变。
// 自愈（真机内存是唯一判据）：失败即**持久降一档**（2→1.5→1.25→1），成功保持不回抬，
//   下次导出自动用更低倍率重试——宁可降清晰度，不再崩。
const ZENITH_FOV_DEG = 188
// 导出渲染**逻辑窗口**边长（窗口单位 / CSS px）。固定它 = 标签相对大小与设备无关
// （越小字越大），这是 M-SW38-b 的契约，M-SW58 起保持不变。
const EXPORT_SIZE = 1600
// ── [M-SW58] 像素倍率（清晰度）：把「逻辑窗口」与「物理像素」解耦 ──────────────
// M-SW57 为修 iPhone 崩溃，把后备缓冲一刀切钉成 EXPORT_SIZE²（1600²），代价是**所有
// 设备都被压平**——连桌面 Retina 上原本的 3200² 也一起丢了。本批把两者拆开：
//   fb_size = EXPORT_SIZE × SCALE，投影与标签仍按 EXPORT_SIZE 算（相对大小不变）。
//
// 档位必须受**面积硬限**约束：iOS/Safari 对单个 canvas 有 16,777,216 px（4096²）面积
// 硬限，越界即静默失效或直接被 Jetsam 秒杀。M-SW55 的 1600×3 = 4800² = 2306 万像素
// 正踩此线——这才是「一点导出就崩」的直接真因（比内存更硬，且不可绕过）。
//   · 桌面默认 2×  → 3200² = 1024 万像素（距硬限 39% 余量；桌面内存宽松）；
//   · 移动默认 1.5× → 2400² = 576 万像素（余量 66%；因引擎开 MSAA 4x + depth24stencil8
//     会成倍放大占用，而 iOS 全 canvas 总内存另有 ≈384MB 上限，故移动端求稳）。
const EXPORT_SCALE_LADDER = [1, 1.25, 1.5, 2]
const EXPORT_SCALE_MOBILE = 1.5
const EXPORT_SCALE_DESKTOP = 2
// 后备边长安全上限（px）：4096 为 iOS 硬限，取 3840 留 ≈12% 余量（Apple 社区推荐保守阈值）。
const EXPORT_EDGE_MAX = 3840
// [M-SW58] 像素倍率上限索引的持久化键：某档若在真机上崩过（页面被杀），下次打开自动降一档
// 并持久保存，避免同一台设备反复崩；成功后保持当前档（不回抬），稳定优先。
const SCALE_CAP_KEY = 'gx.export.scaleCap'
// M-SW57 真机面包屑：分阶段记录导出进度到 localStorage。页面若中途被系统杀掉，
// 下次打开可回读中断点（真机再崩时能拿到确切位置，不必再靠推断）。
const BREADCRUMB_KEY = 'gx.export.breadcrumb'
const BREADCRUMB_MAX_AGE_MS = 7 * 24 * 3600 * 1000

// ── M-SW38-c：星空文化就绪门（取代原先「固定等 2s」）────────────────
// 轮询间隔 / 等待上限；就绪后还要等连线淡入动画（引擎 fader 约 0.5s）走完；
// 若就绪信号不可用（引擎结构变化）则退化为固定等待，绝不把导出卡死。
const READY_POLL_MS = 150
const READY_MAX_MS = 6000
const READY_SETTLE_MS = 1200
const READY_FALLBACK_MS = 2000

export default {
  name: 'star-map-export-dialog',
  data: function () {
    return {
      busy: false,
      error: '',
      hint: '',
      // M-SW57：上一次导出若中途中断（页面被系统杀掉），这里显示中断阶段，便于真机定位。
      staleNotice: '',
      westImg: null,
      chineseImg: null
    }
  },
  // M-SW57：组件挂载即回读上次导出的面包屑——若上次没走到 done，说明页面被杀过。
  mounted: function () {
    this.readStaleBreadcrumb()
  },
  watch: {
    // 底栏按钮只翻转 store 开关；对话框打开即开始生成（关闭再开会重新生成，
    // 保证星空取的是当下时刻）。
    '$store.state.showStarMapExportDialog': function (v) {
      if (v && !this.busy) this.generate()
    }
  },
  beforeDestroy: function () {
    // 防御：组件销毁时渲染钩子、导出尺寸、FOV 钳制都必须归位
    this.uninstallCaptureHook()
    this.endExportSize()
    swh.setFovClampEnabled(true)
  },
  methods: {
    close: function () {
      this.$store.commit('setValue', { varName: 'showStarMapExportDialog', newValue: false })
    },
    // [guanxing patch] M-SW55：保存统一走共享模块 image-save。
    // iOS 弹系统分享面板（可「存储图像」到照片、或「存到文件」保留文件名）；
    // 其它端走 blob:URL 下载。旧版把数 MB 的 data:URL 直接塞进 <a href download>，
    // iOS 会当「打开图片」，用户只能长按存到相册（用户报告的现象），且大字符串吃内存。
    save: async function (dataUrl, name) {
      this.hint = ''
      const r = await saveImage(dataUrl, name)
      if (r && r.blocked) {
        // 系统分享被拒（典型：同一次手势里发起的第二次分享没有 transient activation）
        this.hint = '系统把这次保存拦下了——请再点一次「保存这一张」'
      }
    },
    // 「两张都保存」：iOS 一次手势只允许一次系统分享，第二张可能被拦下。
    // 逐张保存，被拦时给「再点一次」的引导（不静默失败）。
    saveAll: async function () {
      this.hint = ''
      const r1 = await saveImage(this.westImg, '星图-星座连线.png')
      if (r1 && r1.aborted) return // 用户在第一张的分享面板取消，尊重其意图
      const r2 = await saveImage(this.chineseImg, '星图-星官连线.png')
      if (r2 && r2.blocked) {
        this.hint = '第一张已保存；第二张被系统拦下——请再点一次「两张都保存」，' +
          '或直接点第二张下方的「保存这一张」'
      }
    },
    // ── 截图钩子 ──────────────────────────────────────────────
    installCaptureHook: function () {
      const stel = this.$stel
      const self = this
      this.$_origCoreRender = stel._core_render
      stel._core_render = function (w, h, dpr) {
        // M-SW38 修正：引擎的 lock（navigation.c:116-123）与视角动画都在本帧
        // 更早的 _core_update() 里跑，会改写 observer.yaw/pitch。渲染前再写一次
        // 天顶朝向作双保险——即使某些路径下 lock 未清干净，取到的仍是正上方全景。
        if (self.$_forceZenith) {
          const obs = stel.core.observer
          obs.yaw = 0
          obs.pitch = Math.PI / 2
        }
        // [M-SW58] 导出期间把「逻辑窗口单位」与「像素倍率」解耦，不再透传全局 dpr。
        // 引擎 fb_size = win_size × pixel_scale（core.c:527-576），而钩子收到的 w/h 是
        // rect 尺寸（= 后备像素 EXPORT_SIZE × SCALE，见 beginExportSize 的 rect 补丁）；
        // 若原样透传，win_size 会被当成 SCALE 倍 → 标签相对变小，且 M-SW55 时代透传 dpr
        // 还会在 iOS 得到 1600×3 = 4800²（2306 万像素）——越过 iOS/Safari 单 canvas 4096²
        // 面积硬限，正是那条致命路径。故这里还原：
        //   win_size = EXPORT_SIZE（投影与标签比例不变）
        //   pixel_scale = SCALE（清晰度，受档位与面积上限约束）
        //   ⇒ fb_size = EXPORT_SIZE × SCALE = 后备边长 edge ✓，glViewport(0,0,edge,edge) ✓
        const winW = self.$_forcePixelScale ? EXPORT_SIZE : w
        const winH = self.$_forcePixelScale ? EXPORT_SIZE : h
        const scale = self.$_forcePixelScale ? self.$_exportScale : dpr
        // M-SW57/M-SW58 面包屑：记录导出后第一帧的真实入参（逻辑窗口、像素倍率、
        // 后备尺寸、当前全局 dpr），真机再崩时这是唯一能拿到的现场。
        if (self.$_forcePixelScale && !self.$_frameLogged) {
          self.$_frameLogged = true
          const cv = stel.canvas || document.querySelector('canvas')
          self.bc('frame-1', {
            win_w: winW,
            win_h: winH,
            pixel_scale: scale,
            canvas_w: cv ? cv.width : null,
            canvas_h: cv ? cv.height : null
          })
        }
        const r = self.$_origCoreRender.call(stel, winW, winH, scale)
        // 同一任务内取像：preserveDrawingBuffer=false 下，浏览器在本次事件
        // 循环结束后才可能清缓冲，这里同步 toDataURL 必然有效。
        if (self.$_capture) {
          const pending = self.$_capture
          self.$_capture = null
          try {
            const canvas = stel.canvas || document.querySelector('canvas')
            pending.resolve(canvas.toDataURL('image/png'))
          } catch (e) {
            pending.reject(e)
          }
        }
        return r
      }
    },
    uninstallCaptureHook: function () {
      if (this.$stel && this.$_origCoreRender) {
        this.$stel._core_render = this.$_origCoreRender
      }
      this.$_origCoreRender = null
      this.$_capture = null
      this.$_forceZenith = false
      this.$_frameLogged = false
    },
    // ── M-SW57 真机面包屑 ─────────────────────────────────────
    // 把导出进度分阶段写进 localStorage。页面若被系统杀掉（iOS jetsam），localStorage
    // 仍留存最后一次写入的阶段，下次打开即可知道「崩在哪一步」。
    bc: function (stage, extra) {
      try {
        const payload = { stage: stage, at: new Date().toISOString() }
        // 记录「真实」全局 dpr（不是我们改写过的值）——这是判断 dpr 覆盖是否生效的直接证据。
        payload.dpr = window.devicePixelRatio
        if (extra) {
          for (const k in extra) payload[k] = extra[k]
        }
        window.localStorage.setItem(BREADCRUMB_KEY, JSON.stringify(payload))
      } catch (e) { /* 隐私模式等 localStorage 不可用：不阻断导出 */ }
    },
    // 回读面包屑：上次若中途中断（未走到 done），说明极可能被 iOS jetsam 杀页（真机上
    // 表现为「一点就崩」）。[M-SW58] 此时把像素倍率档位**持久降一档**，下次导出自动用更低
    // 倍率重试，避免同一台设备反复崩；降档写进提示，用户可感知。
    readStaleBreadcrumb: function () {
      try {
        const raw = window.localStorage.getItem(BREADCRUMB_KEY)
        if (!raw) return
        const b = JSON.parse(raw)
        if (!b || !b.stage) return
        const at = b.at ? Date.parse(b.at) : NaN
        if (!isNaN(at) && (Date.now() - at) > BREADCRUMB_MAX_AGE_MS) return // 太旧忽略
        if (b.stage === 'done') return // 上次成功：不动档位
        const def = this.deviceScaleIndex()
        // 上次记录的实际档（更早版本的面包屑无此字段 → 以设备默认档兜底）
        const prev = (typeof b.downgrade === 'number') ? b.downgrade : def
        const next = Math.max(0, Math.min(prev - 1, def))
        this.writeScaleCap(next)
        const bits = ['上次导出中断于「' + b.stage + '」']
        if (b.dpr != null) bits.push('dpr=' + b.dpr)
        if (b.canvas_w != null) bits.push('canvas=' + b.canvas_w + '×' + b.canvas_h)
        if (b.pixel_scale != null) bits.push('倍率=' + b.pixel_scale + '×')
        if (next < def) bits.push('已自动降到 ' + this.scaleForIndex(next) + '× 重试')
        this.staleNotice = bits.join(' · ')
        console.warn('[M-SW57/M-SW58] 上次导出未完成 →', b, '→ 降档至', this.scaleForIndex(next) + '×')
      } catch (e) { /* 忽略坏数据 */ }
    },
    clearBreadcrumb: function () {
      try { window.localStorage.removeItem(BREADCRUMB_KEY) } catch (e) { /* 忽略 */ }
    },
    // ── [M-SW58] 像素倍率档位 ─────────────────────────────────
    // 是否移动端（决定默认档：移动求稳 1.5×，桌面给满 2×）。
    isMobileDevice: function () {
      try {
        const ua = navigator.userAgent || ''
        if (/iPhone|iPad|iPod|Android|Mobile|Silk/i.test(ua)) return true
        // iPadOS 13+ 桌面级 UA：platform=MacIntel 且支持多点触控
        if (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1) return true
        return false
      } catch (e) { return false }
    },
    // 设备默认档索引（阶梯下标，越大越清晰）
    deviceScaleIndex: function () {
      const target = this.isMobileDevice() ? EXPORT_SCALE_MOBILE : EXPORT_SCALE_DESKTOP
      let idx = 0
      for (let i = 0; i < EXPORT_SCALE_LADDER.length; i++) {
        if (EXPORT_SCALE_LADDER[i] <= target) idx = i
      }
      return idx
    },
    // 档索引 → 倍率（越界收敛到两端）
    scaleForIndex: function (idx) {
      const n = EXPORT_SCALE_LADDER.length
      const i = Math.max(0, Math.min(Math.round(idx) || 0, n - 1))
      return EXPORT_SCALE_LADDER[i]
    },
    readScaleCap: function () {
      try {
        const raw = window.localStorage.getItem(SCALE_CAP_KEY)
        if (raw == null) return null
        const n = parseInt(raw, 10)
        return isNaN(n) ? null : n
      } catch (e) { return null }
    },
    writeScaleCap: function (idx) {
      try { window.localStorage.setItem(SCALE_CAP_KEY, String(idx)) } catch (e) { /* 忽略 */ }
    },
    // 本次实际使用档 = min(设备默认档, 持久降档上限)，再受面积硬限兜底。
    currentScaleIndex: function () {
      const def = this.deviceScaleIndex()
      const cap = this.readScaleCap()
      let idx = (cap == null) ? def : Math.max(0, Math.min(cap, def))
      // 面积守卫：EXPORT_SIZE × scale 不得超过 EXPORT_EDGE_MAX（防御性，阶梯内恒成立）
      while (idx > 0 && EXPORT_SIZE * this.scaleForIndex(idx) > EXPORT_EDGE_MAX) idx--
      return idx
    },
    // 等下一渲染帧取像（rAF 循环持续在跑，立即挂起等钩子回调）
    capture: function () {
      return new Promise((resolve, reject) => {
        this.$_capture = { resolve: resolve, reject: reject }
        setTimeout(() => {
          if (this.$_capture) {
            this.$_capture = null
            reject(new Error('渲染帧超时'))
          }
        }, 5000)
      })
    },
    // ── 星空文化就绪（M-SW38-c）───────────────────────────────
    // 读当前星空文化的可读信号。为什么这么取见文件头 M-SW38-c ② 的实测记录：
    //   available —— 能否读到 current 对象（读不到 = 信号不可用，调用方退化处理）
    //   name      —— index.json 是否已解析（空 = 数据未到货）
    //   key       —— 引擎当前的文化键，用于确认切换真的生效（而不是停在旧文化上）
    readActiveCultureProbe: function () {
      try {
        const sc = this.$stel && this.$stel.core && this.$stel.core.skycultures
        if (!sc) return { available: false, name: '', key: '' }
        const cur = sc.current
        return {
          available: !!(cur && typeof cur === 'object'),
          name: (cur && typeof cur.name === 'string') ? cur.name.trim() : '',
          key: (typeof sc.current_id === 'string') ? sc.current_id : ''
        }
      } catch (e) {
        return { available: false, name: '', key: '' }
      }
    },
    // 等目标星空文化真正可用：current_id 已切到 key 且该文化的 name 已解析。
    // 就绪后再等 READY_SETTLE_MS 让连线淡入（引擎 fader 约 0.5s）走完。
    waitCultureReady: function (key) {
      const self = this
      const started = Date.now()
      return new Promise((resolve, reject) => {
        let everAvailable = false
        const tick = function () {
          const p = self.readActiveCultureProbe()
          if (p.available) everAvailable = true
          if (p.available && p.name && p.key === key) {
            setTimeout(resolve, READY_SETTLE_MS)
            return
          }
          if (Date.now() - started >= READY_MAX_MS) {
            if (everAvailable) {
              // 信号可用、但目标文化始终没解析出来 → 不拍缺线坏图（M-SW39 同纪律）
              reject(new Error('星空文化「' + key + '」数据未就绪，请检查网络后重试'))
            } else {
              // 信号不可用（引擎结构变化）：退化为固定等待，不阻断导出
              console.warn('[M-SW38-c] 星空文化就绪信号不可用，退化为固定等待')
              setTimeout(resolve, READY_FALLBACK_MS)
            }
            return
          }
          setTimeout(tick, READY_POLL_MS)
        }
        tick()
      })
    },
    // ── 导出尺寸（M-SW38-b 固定窗口；M-SW57 与 dpr 解耦；M-SW58 倍率可调）──────
    // 目标：导出期间后备缓冲 = EXPORT_SIZE × SCALE（桌面 3200² / 移动 2400²），与设备
    // devicePixelRatio 彻底解耦——这既是 iPhone「一点导出就崩溃」的修法，也是「恢复高清」的实现。
    //
    // 引擎事实（源码为证）：
    //   · canvas.js:28-41 渲染循环每帧：
    //       dpr  = window.devicePixelRatio
    //       rect = canvas.getBoundingClientRect()
    //       if (canvas.width !== rect.width || canvas.height !== rect.height)
    //           canvas.width = rect.width * dpr; canvas.height = rect.height * dpr
    //       _core_render(rect.width, rect.height, dpr)
    //   · core.c:527-576 core_render(win_w, win_h, pixel_scale)：
    //       core->win_size = {win_w, win_h}
    //       painter.fb_size = {win_w*pixel_scale, win_h*pixel_scale}
    //       render_prepare(...) → rend->fb_size = {win_w*scale, win_h*scale}（render_gl.c:371）
    //       render_gl.c:1725 glViewport(0, 0, fb_size[0], fb_size[1])
    //   即：后备缓冲 = 窗口单位 × pixel_scale，而 pixel_scale 由调用方传入。
    //   M-SW55 曾用 defineProperty 覆盖全局 dpr——真机证伪（覆盖若未生效，1600×3=4800²
    //   ≈2306 万像素、越过 iOS 单 canvas 4096² 面积硬限，被 jetsam 秒杀）。
    //
    // 自持方案，不依赖任何全局覆盖：
    //   ① CSS 尺寸固定 EXPORT_SIZE 方形 → 逻辑窗口恒 1600（标签相对大小与设备无关）；
    //   ② 显式把后备缓冲钉为 canvas.width = canvas.height = EXPORT_SIZE × SCALE；
    //   ③ canvas.getBoundingClientRect 补丁（宽高回后备像素）→ 引擎 sizeChanged 判定恒 false，
    //      既挡住 `rect.width * dpr` 的超大分配，也挡住任何外部 CSS 把 CSS 宽改成非 1600；
    //   ④ 见 installCaptureHook：渲染时把入参还原成 (EXPORT_SIZE, EXPORT_SIZE, SCALE)，
    //      使 fb_size = EXPORT_SIZE × SCALE，与后备一致（M-SW57 此处为强制 pixel_scale=1）。
    beginExportSize: function () {
      const stel = this.$stel
      const canvas = stel.canvas || document.querySelector('canvas')
      if (!canvas) return
      const self = this
      this.$_origCanvasStyle = { width: canvas.style.width, height: canvas.style.height }
      this.$_origCanvasSize = { width: canvas.width, height: canvas.height }
      this.$_origGetBCR = canvas.getBoundingClientRect
      // [M-SW58] 本次导出的像素倍率与后备边长（由主流程按「设备默认档 ∩ 持久降档」算出）
      const scale = this.scaleForIndex(this.$_exportScaleIndex)
      const edge = EXPORT_SIZE * scale
      this.$_exportScale = scale
      this.$_exportEdge = edge
      // ① 逻辑窗口单位固定为 EXPORT_SIZE 方形（标签相对大小与设备无关）
      canvas.style.width = EXPORT_SIZE + 'px'
      canvas.style.height = EXPORT_SIZE + 'px'
      // ② 后备缓冲显式钉为 edge²（= EXPORT_SIZE × scale）——与设备 dpr 无关。
      //    仍写成 `EXPORT_SIZE * scale`：显式钉尺寸（M-SW57 契约）不变，倍率可控（M-SW58）。
      canvas.width = EXPORT_SIZE * scale
      canvas.height = EXPORT_SIZE * scale
      // ③ getBoundingClientRect 补丁：宽高返回后备像素尺寸。引擎的 sizeChanged 判定
      //    因此恒为 false，`canvas.width = rect.width * dpr` 永不执行（那正是 iOS 上把
      //    后备撑到 4800² 的致命分配）。保留 left/top 等位置字段，鼠标映射不受影响。
      canvas.getBoundingClientRect = function () {
        const r = self.$_origGetBCR.call(canvas)
        return {
          left: r.left,
          top: r.top,
          right: r.right,
          bottom: r.bottom,
          x: r.x,
          y: r.y,
          width: canvas.width,
          height: canvas.height
        }
      }
      // ④ 打开「渲染钩子里强制 pixel_scale=1」开关，并记录尺寸阶段的真机面包屑
      this.$_forcePixelScale = true
      this.$_frameLogged = false
      this.bc('size-done', { canvas_w: canvas.width, canvas_h: canvas.height, css: EXPORT_SIZE, scale: scale })
    },
    endExportSize: function () {
      const stel = this.$stel
      const canvas = stel && (stel.canvas || document.querySelector('canvas'))
      if (canvas) {
        if (this.$_origCanvasStyle) {
          canvas.style.width = this.$_origCanvasStyle.width
          canvas.style.height = this.$_origCanvasStyle.height
        }
        if (typeof this.$_origGetBCR === 'function') {
          canvas.getBoundingClientRect = this.$_origGetBCR
        }
        // 后备尺寸还原；引擎下一帧会按真实 rect × dpr 重新分配（自愈）
        if (this.$_origCanvasSize) {
          canvas.width = this.$_origCanvasSize.width
          canvas.height = this.$_origCanvasSize.height
        }
      }
      this.$_forcePixelScale = false
      this.$_frameLogged = false
      this.$_origCanvasStyle = null
      this.$_origCanvasSize = null
      this.$_origGetBCR = null
      this.$_exportScale = null
      this.$_exportEdge = null
    },
    // ── 后处理：裁方 + 地平线圈外遮罩（M-SW38-b）──────────────
    // ① 居中裁成方形（防备后备像素取整带来的 1px 偏差）；
    // ② 把「地平线投影圆」以外填成地景底色——引擎标签是最后一道 2D 绘制、
    //    且不判断地平线（labels.c: should_render_label 只判画面矩形），地平线
    //    以下的星座名/星官名会漏画到黑盘上，这一步把它们一次性抹掉。
    //    地景是近乎纯黑的圆盘，填色视觉无损。
    // 半径：立体投影 r(α)=tan(α/2)/tan(fov/4)（projections/proj_stereographic.c），
    // 地平线 α=90°；方形窗口下 core.fov 即垂直视场。
    postProcess: function (dataUrl) {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const s = Math.min(img.width, img.height)
          const c = document.createElement('canvas')
          c.width = s
          c.height = s
          const ctx = c.getContext('2d')
          ctx.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, s, s)
          const R = (1 / Math.tan((ZENITH_FOV_DEG / 4) * Math.PI / 180)) * (s / 2)
          ctx.save()
          ctx.beginPath()
          ctx.rect(0, 0, s, s) // 整个方形
          ctx.arc(s / 2, s / 2, R, 0, Math.PI * 2, true) // 反向圆 → 只填圆外
          ctx.fillStyle = this.sampleLandscapeColor(ctx, s)
          ctx.fill()
          ctx.restore()
          resolve(c.toDataURL('image/png'))
        }
        img.onerror = () => resolve(dataUrl) // 处理失败退回原图
        img.src = dataUrl
      })
    },
    // 取画面四角的像素逐通道取最小值，得到与当前渲染一致的地景底色
    // （地景是画面最暗的部分；四角必在遮罩圆外）。
    sampleLandscapeColor: function (ctx, s) {
      const pts = [[2, 2], [s - 3, 2], [2, s - 3], [s - 3, s - 3]]
      let r = 255
      let g = 255
      let b = 255
      for (let i = 0; i < pts.length; i++) {
        const d = ctx.getImageData(pts[i][0], pts[i][1], 1, 1).data
        r = Math.min(r, d[0])
        g = Math.min(g, d[1])
        b = Math.min(b, d[2])
      }
      return 'rgb(' + r + ',' + g + ',' + b + ')'
    },
    // ── 场景与状态 ────────────────────────────────────────────
    saveState: function () {
      const core = this.$stel.core
      const ls = core.landscapes
      this.$_saved = {
        fov: core.fov,
        yaw: core.observer.yaw,
        pitch: core.observer.pitch,
        sc: core.skycultures.current_id,
        storeSc: this.$store.state.skycultureId,
        lines: core.constellations.lines_visible,
        labels: core.constellations.labels_visible,
        images: core.constellations.images_visible,
        gridAz: core.lines.azimuthal.visible,
        gridEqJ: core.lines.equatorial_jnow.visible,
        gridEq: core.lines.equatorial.visible,
        ecliptic: core.lines.ecliptic.visible,
        meridian: core.lines.meridian.visible,
        landOpacity: ('opacity' in ls) ? ls.opacity : undefined,
        landHideBelow: ('hide_below_horizon' in ls) ? ls.hide_below_horizon : undefined,
        landVisible: ls.visible,
        // M-SW38 修正：lock 是「每帧把视角拉回该天体」的引擎开关，必须一并
        // 暂存并清空，否则天顶视角下一帧就被覆盖（navigation.c:116-123）。
        lock: core.lock,
        selection: core.selection
      }
    },
    restoreState: function () {
      const s = this.$_saved
      if (!s || !this.$stel) return
      const core = this.$stel.core
      this.$_forceZenith = false
      // 星空文化先回（数据已缓存即刻生效），再逐层写回
      core.skycultures.current_id = s.sc
      this.$store.commit('setValue', { varName: 'skycultureId', newValue: s.storeSc })
      core.constellations.lines_visible = s.lines
      core.constellations.labels_visible = s.labels
      core.constellations.images_visible = s.images
      core.lines.azimuthal.visible = s.gridAz
      core.lines.equatorial_jnow.visible = s.gridEqJ
      core.lines.equatorial.visible = s.gridEq
      core.lines.ecliptic.visible = s.ecliptic
      core.lines.meridian.visible = s.meridian
      const ls = core.landscapes
      if (s.landOpacity !== undefined && 'opacity' in ls) ls.opacity = s.landOpacity
      if (s.landHideBelow !== undefined && 'hide_below_horizon' in ls) ls.hide_below_horizon = s.landHideBelow
      ls.visible = s.landVisible
      if (s.selection) core.selection = s.selection
      if (s.lock) core.lock = s.lock
      // 视角最后写：若恢复了 lock，引擎要下一帧才会把朝向拉回该天体，
      // 这里先把 yaw/pitch/fov 写回原值，用户眼前状态即时还原。
      core.observer.pitch = s.pitch
      core.observer.yaw = s.yaw
      core.fov = s.fov
      this.$_saved = null
    },
    // 切到「天顶全景」导出场景。sc: 'western'（星座连线）| 'chinese'（中国星官连线）
    applyScene: function (sc) {
      const core = this.$stel.core
      // M-SW38 修正：先解除「每帧锁定该天体」——否则下面写的天顶朝向会被
      // core_update_direction 在下一帧覆盖（用户反馈「不是朝向正上方」的根因）；
      // 同时清掉选中高亮圈，避免高亮框出现在成品星图里。
      core.lock = null
      core.selection = null
      core.skycultures.current_id = sc
      this.$store.commit('setValue', { varName: 'skycultureId', newValue: sc })
      core.constellations.lines_visible = true
      core.constellations.labels_visible = true
      core.constellations.images_visible = false // 不开艺术图（用户定案）
      // 网格/刻度线临时关闭，保持星图干净（恢复走 restoreState）
      core.lines.azimuthal.visible = false
      core.lines.equatorial_jnow.visible = false
      core.lines.equatorial.visible = false
      core.lines.ecliptic.visible = false
      core.lines.meridian.visible = false
      // 地景全显形成边框：解除地平线下载剪、不透明度拉满
      // （M-SW36 补的引擎属性；旧产物降级为 visible 布尔）
      const ls = core.landscapes
      if ('opacity' in ls) ls.opacity = 1
      if ('hide_below_horizon' in ls) ls.hide_below_horizon = false
      else ls.visible = true
      // 正上方天顶 + 全景视场：yaw=0 让朝向确定（两张图构图一致），
      // fov=188° 使星空圆盘几乎铺满、外圈只留一圈细地景边框（M-SW38-b 收窄）。
      core.observer.yaw = 0
      core.observer.pitch = Math.PI / 2
      core.fov = ZENITH_FOV_DEG * Math.PI / 180
      this.$_forceZenith = true
    },
    // [guanxing patch] M-SW63：这一帧画面是哪一刻（Unix 毫秒），供右上角日期印章。
    // 时间源 = 引擎 core.observer.utc（Stellarium 口径为 MJD），换算见 image-date.js。
    // 取不到（引擎未就绪）返回 NaN ⇒ civilDateText 给空串 ⇒ 跳过印章，不产坏图。
    stampMs: function () {
      const mjd = this.$stel && this.$stel.core ? this.$stel.core.observer.utc : NaN
      return mjdToUnixMs(mjd)
    },
    // ── 主流程 ────────────────────────────────────────────────
    generate: async function () {
      this.busy = true
      this.error = ''
      this.hint = ''
      this.staleNotice = ''
      this.westImg = null
      this.chineseImg = null
      // [M-SW58] 本次导出的像素倍率档（设备默认档 ∩ 持久降档 ∩ 面积上限）
      this.$_exportScaleIndex = this.currentScaleIndex()
      // M-SW57/M-SW58：面包屑起点——记下本次档位；若页面此后被杀，下次据此自动降档重试。
      this.bc('start', {
        scale: this.scaleForIndex(this.$_exportScaleIndex),
        downgrade: this.$_exportScaleIndex
      })
      try {
        this.saveState()
        this.beginExportSize()
        this.installCaptureHook()
        // 抬 fov 到 188° 前必须旁路移动端 120° 钳制守卫（sw_helpers onReady），
        // finally 里无条件恢复，防止导出失败后移动端缩放越界。
        swh.setFovClampEnabled(false)
        // 图1：西方星座连线。applyScene 里 current_id 是同步切换的，就绪轮询
        // 只需等「目标文化已解析」+ 连线淡入（M-SW38-c：取代原先的固定 1.5s/2s）。
        this.applyScene('western')
        await this.waitCultureReady('western')
        this.bc('capture-1')
        // M-SW41：底部拼品牌页脚（banner 按截图宽度等比缩放；失败退回原图）。
        // M-SW63：第三参 = 这一帧的引擎时刻，右上角盖公历日期。
        this.westImg = await appendFooter(
          await this.postProcess(await this.capture()),
          footerUrl('star1.jpg'),
          this.stampMs()
        )
        this.bc('capture-1-done')
        // 图2：中国星官连线 —— 必须与界面底栏「中国星官」同源（chinese，310 星官）。
        // 原用 chinese-28（二十八宿）是「标题写星官、画的是二十八宿」的缺陷根因
        // （用户反馈「截图 中国星官连线 时 连线不同」）。chinese-28 数据启动时也已
        // 注册，但导出不再使用。
        this.applyScene('chinese')
        await this.waitCultureReady('chinese')
        this.bc('capture-2')
        this.chineseImg = await appendFooter(
          await this.postProcess(await this.capture()),
          footerUrl('star2.png'),
          this.stampMs()
        )
        this.bc('done')
      } catch (e) {
        console.error('[M-SW38] 把这片星空带走失败:', e)
        this.error = (e && e.message) ? e.message : String(e)
        this.bc('error', { message: this.error })
      } finally {
        this.restoreState()
        this.uninstallCaptureHook()
        swh.setFovClampEnabled(true)
        this.endExportSize()
        this.busy = false
      }
    }
  }
}
</script>

<style>
/* [guanxing patch] M-SW38：导出星图对话框样式（沿用 M-SW12 token）。 */
.gx-export-hint {
  color: var(--gx-text-dim, rgba(255, 255, 255, 0.6));
  font-size: 13px;
  line-height: 18px;
  margin-bottom: 12px;
}
.gx-export-busy {
  padding: 24px 4px;
  color: var(--gx-text, #f2f2f2);
}
.gx-export-error {
  color: #ff8a80;
  padding: 16px 4px;
}
/* [guanxing patch] M-SW55：iOS「两张都保存」第二张被系统拦截时的引导文案。 */
.gx-export-hint-err {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(120, 90, 30, 0.28);
  border: 1px solid rgba(255, 200, 120, 0.32);
  color: #ffd9a0;
  font-size: 13px;
  line-height: 19px;
}
/* [guanxing patch] M-SW57：上次导出中断的真机面包屑提示。 */
.gx-export-stale {
  margin-bottom: 10px;
  padding: 7px 10px;
  border-radius: 6px;
  background: rgba(90, 40, 40, 0.32);
  border: 1px solid rgba(255, 140, 140, 0.3);
  color: #ffb3b3;
  font-size: 12px;
  line-height: 17px;
  word-break: break-all;
}
.gx-export-cell {
  padding: 6px;
}
.gx-export-img {
  width: 100%;
  display: block;
  background: #000;
  border-radius: 6px;
  border: 1px solid var(--gx-panel-border, rgba(255, 255, 255, 0.14));
}
.gx-export-caption {
  margin: 8px 2px;
  color: var(--gx-text, #f2f2f2);
  font-size: 14px;
}
</style>
